import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatFCFA, statusLabel, resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CheckCircle2, Mail, Store, Loader2, AlertCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;
// While the order is awaiting acceptance, keep polling so the customer page
// auto-refreshes the moment the admin accepts or refuses (no manual refresh needed).
const STATUS_POLL_INTERVAL = 4000;

export default function Confirmation() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const pollRef = useRef(null);
  const statusPollRef = useRef(null);
  const lastStatusRef = useRef(null);
  const celebratedRef = useRef(false);

  const loadOrder = useCallback(async () => {
    try {
      const r = await api.get(`/orders/${orderId}`);
      setOrder(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  useEffect(() => {
    if (!sessionId || !polling) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setPaymentStatus(r.data.payment_status);
        // With manual capture: payment_status="authorized" = card OK, waiting for admin
        // Customer's checkout flow is complete in both cases (paid OR authorized)
        if (r.data.payment_status === "paid" || r.data.payment_status === "authorized") {
          setPolling(false);
          await loadOrder();
          return;
        }
        if (r.data.status === "expired") {
          setPolling(false);
          return;
        }
      } catch {
        /* network blip */
      }
      if (cancelled) return;
      setPollCount((c) => {
        if (c + 1 >= MAX_POLLS) {
          setPolling(false);
          return c + 1;
        }
        pollRef.current = setTimeout(poll, POLL_INTERVAL);
        return c + 1;
      });
    };
    // Defer first call to the next tick so we never call setState synchronously inside the effect.
    pollRef.current = setTimeout(poll, 0);
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, polling]);

  // ---- Real-time status polling: keep watching while the order is awaiting acceptance ----
  // When admin accepts -> status becomes "paid"/"preparing" -> we auto-refresh + celebrate.
  // When admin rejects -> status becomes "cancelled" -> we show the rejection screen + reason.
  useEffect(() => {
    if (!order) return;
    const pStatus = order.payment_status;
    const oStatus = order.status;
    const isAwaiting = (pStatus === "authorized") || (oStatus === "awaiting_acceptance");
    // Detect transitions from "awaiting" -> "accepted" / "rejected"
    const prev = lastStatusRef.current;
    if (prev && prev.isAwaiting && !isAwaiting && !celebratedRef.current) {
      celebratedRef.current = true;
      if (oStatus === "cancelled") {
        toast.error("The restaurant could not fulfill your order. You will not be charged.", { duration: 6000 });
        // Light vibration on phones
        try { if (navigator.vibrate) navigator.vibrate([200, 80, 200]); } catch { /* ignore */ }
      } else {
        toast.success("Your order has been accepted! Preparation has started.", { duration: 6000 });
        try { if (navigator.vibrate) navigator.vibrate([120, 60, 120, 60, 240]); } catch { /* ignore */ }
      }
    }
    lastStatusRef.current = { isAwaiting, oStatus, pStatus };

    if (!isAwaiting) return; // No need to keep polling
    statusPollRef.current = setTimeout(() => { loadOrder(); }, STATUS_POLL_INTERVAL);
    return () => { if (statusPollRef.current) clearTimeout(statusPollRef.current); };
  }, [order, loadOrder]);

  if (!order && !sessionId) {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-32 text-center text-[var(--dsf-text-muted)]">Chargement…</div>
      </div>
    );
  }

  // While polling Stripe status
  if (sessionId && polling) {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-40 pb-20 px-6 text-center" data-testid="confirmation-polling">
          <Loader2 size={42} className="text-[var(--dd-green)] mx-auto animate-spin mb-6" />
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--dd-green)] mb-3">Confirmation du paiement…</h1>
          <p className="text-[var(--dsf-text-muted)]">Merci de patienter quelques instants pendant que Stripe confirme votre transaction.</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Polling timeout / failed
  if (sessionId && !polling && paymentStatus && paymentStatus !== "paid" && paymentStatus !== "authorized") {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-40 pb-20 px-6 text-center max-w-2xl mx-auto" data-testid="confirmation-failed">
          <AlertCircle size={42} className="text-[var(--dd-coral)] mx-auto mb-6" />
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--dd-coral)] mb-3">Paiement non confirmé</h1>
          <p className="text-[var(--dsf-text-muted)] mb-6">Votre paiement Stripe n&apos;a pas pu être confirmé. Votre commande {order?.order_number} reste enregistrée. Vous pouvez réessayer ou nous contacter.</p>
          <Link to="/checkout" className="btn-primary">Réessayer le paiement</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isAwaitingAcceptance = order?.payment_status === "authorized" || order?.status === "awaiting_acceptance";
  const isRejected = order?.status === "cancelled" && (order?.rejection_reason || order?.payment_status === "cancelled");

  if (!order) return null;

  // -------- Rejected state (admin refused) --------
  if (isRejected) {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-32 pb-20 px-6 md:px-12 lg:px-20" data-testid="confirmation-rejected">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center mb-6 rounded-full bg-[var(--dd-coral)]/15">
              <XCircle size={32} className="text-[var(--dd-coral)]" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dd-coral)] mb-3">Order cancelled</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--dd-coral)] mb-4">We&apos;re sorry, {order.customer_name.split(" ")[0]}</h1>
            <p className="text-[var(--dsf-text-secondary)] text-base md:text-lg">
              Our team was unable to fulfill order <span className="font-semibold" data-testid="rejected-order-number">{order.order_number}</span>.
              <strong className="block mt-2 text-[var(--dd-green)]">You have not been charged</strong> — the card authorization has been released and any pending charge will disappear from your statement within 1–3 business days.
            </p>
            {order.rejection_reason && (
              <div className="mt-8 mx-auto max-w-md rounded-2xl bg-[var(--dd-bone)] border-l-4 border-[var(--dd-coral)] p-5 text-left" data-testid="rejection-reason">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-text-muted)] font-bold mb-2">Reason from the kitchen</div>
                <div className="text-sm text-[var(--dd-green)]">{order.rejection_reason}</div>
              </div>
            )}
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-primary">Back to home</Link>
              <Link to="/checkout" className="btn-pill-outline">Try another order</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-20" data-testid="confirmation-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className={`w-16 h-16 mx-auto flex items-center justify-center mb-6 rounded-full ${isAwaitingAcceptance ? "bg-[var(--dd-mustard)]/20" : "bg-[var(--dsf-success)]/15"}`}>
              {isAwaitingAcceptance ? (
                <Loader2 size={32} className="text-[var(--dd-mustard)] animate-spin" />
              ) : (
                <CheckCircle2 size={32} className="text-[var(--dsf-success)]" />
              )}
            </div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">
              {isAwaitingAcceptance ? "Awaiting restaurant confirmation" : "Order confirmed"}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl text-[var(--dd-green)] mb-4">Merci, {order.customer_name.split(" ")[0]}</h1>
            <p className="text-[var(--dsf-text-secondary)] text-lg">
              {isAwaitingAcceptance ? (
                <>Your card has been <strong>authorized but not yet charged</strong>. Our team will accept the order shortly — you&apos;ll receive a confirmation email when it&apos;s accepted. <br/>Order ID: <span className="font-semibold text-[var(--dd-green)]" data-testid="order-number">{order.order_number}</span></>
              ) : (
                <>Votre commande <span className="font-semibold text-[var(--dd-green)]" data-testid="order-number">{order.order_number}</span> a été reçue et le paiement confirmé.</>
              )}
            </p>
            {isAwaitingAcceptance && (
              <>
                <div className="mt-6 inline-block px-5 py-3 rounded-2xl bg-amber-50 border border-amber-300 text-sm text-amber-900 max-w-md text-left" data-testid="awaiting-banner">
                  ℹ️ If the restaurant cannot fulfill your order, the authorization will be cancelled and you&apos;ll be refunded automatically — no charge.
                </div>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[var(--dsf-text-muted)]" data-testid="awaiting-live-indicator">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--dd-green)] opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--dd-green)]" />
                  </span>
                  <span>This page updates automatically — no need to refresh.</span>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <InfoCard icon={Mail} label="Email" value={order.customer_email} />
            <InfoCard
              icon={Store}
              label="Ramassage"
              value={
                order.delivery_method === "pickup_foodtruck"
                  ? "Food Truck — Houston, TX"
                  : "Restaurant — 2923 Walnut Bend Ln, Houston, TX 77042"
              }
            />
            <InfoCard icon={CheckCircle2} label="Statut" value={statusLabel(order.status)} />
          </div>

          <div className="card-light p-8">
            <h2 className="font-serif text-2xl text-[var(--dd-green)] mb-6">Détail de la commande</h2>
            <div className="space-y-4 mb-6">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-4 pb-4 border-b border-black/5">
                  <img src={resolveImage(it.image)} alt={it.name} className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-sm text-[var(--dsf-text-muted)]">x{it.quantity}</div>
                  </div>
                  <div className="font-medium">{formatFCFA(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Sous-total</span><span>{formatFCFA(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>{order.delivery_method === "pickup_foodtruck" ? "Ramassage Food Truck" : "Ramassage Restaurant"}</span><span>Gratuit</span></div>
              {order.discount > 0 && (
                <div className="flex justify-between text-[var(--dsf-success)]"><span>Réduction</span><span>-{formatFCFA(order.discount)}</span></div>
              )}
              <div className="flex justify-between items-baseline pt-3 border-t border-black/5 mt-3">
                <span className="font-serif text-xl">Total</span>
                <span className="font-serif text-2xl font-semibold text-[var(--dd-green)]">{formatFCFA(order.total)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-black/5">
              <span className={`status-pill status-${order.status}`}>{statusLabel(order.status)}</span>
              <span className="text-sm text-[var(--dsf-text-muted)]">Paiement Stripe sécurisé</span>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/" className="btn-primary">Retour à l&apos;accueil</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="card-light p-5 flex items-start gap-3">
    <Icon size={18} className="text-[var(--dsf-orange)] mt-1 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-text-muted)] mb-1">{label}</div>
      <div className="text-sm font-medium break-words">{value}</div>
    </div>
  </div>
);
